// BL-177 Step 2j (C): the "Schedule send" popup, opened from the composer's Send menu ("Schedule send…") and by "Edit" on a scheduled bubble.
// It IS a popup, so it is built on Modal3D (medium tile, Peacock strip, Escape / focus trap / hand-back of focus come from there).
//
//   a month CALENDAR (today marked, past days and days beyond 90 days off) · a TIME box · the TIME ZONE list in the top-right corner
//   (starts as the company zone; changing it re-reads the picked day and time in that zone) · Cancel / Schedule bottom-right.
//
// It opens on TODAY at the next round 15 minutes (7:47 PM -> 8:00 PM), already picked, so Schedule is on whenever that time is valid.
// Plain-English problems appear right under the time box. When the contact's own time zone is known and shows a different clock,
// "Recipient's local time: 5:00 PM PT" sits under the time and follows every change.
//
// Props: now (a function, for tests), companyTimeZone, recipientTimeZone (the contact's IANA zone), initialAt (edit), initialCancelOnReply
// (edit: handed back unchanged so an old value survives; a new message always sends false), title, subtitle, confirmLabel, busy, error
// (a refusal from the server, shown with the other problems), children (extra rows above the calendar, e.g. the message text when editing),
// modalProps (extra attributes for the tile), onConfirm({ at, cancelOnReply, timeZone }), onClose.
// `quietHours` / `showCancelOnReply` are accepted and ignored: staff messages have no quiet-hours hold, and the "cancel if they reply"
// box is gone from the UI (the backend field stays).
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import Button3D from '../ui/Button3D';
import Modal3D from '../ui/Modal3D';
import ScheduleCalendar from './ScheduleCalendar';
import {
    formatScheduledWhen, instantToPicked, isValidTimeZone, nextQuarterHour, parseClock, pickedToInstant, recipientLocalLabel, scheduleDayRange,
    scheduleZoneChoices, validateScheduledTime,
} from '../../lib/scheduledSend';
import './SchedulePanel.css';

const TICK_MS = 20000;
const DEFAULT_ZONE = 'America/New_York';

export default function SchedulePanel({
    now,
    companyTimeZone = DEFAULT_ZONE,
    recipientTimeZone = null,
    initialAt = null,
    initialCancelOnReply = false,
    title = 'Schedule send',
    subtitle = 'Pick a day and a time.',
    confirmLabel = 'Schedule',
    busy = false,
    error = '',
    onConfirm,
    onClose,
    children = null,
    modalProps = {},
    portal = true,
}) {
    const ids = useId();
    const clock = useMemo(() => (typeof now === 'function' ? now : () => new Date()), [now]);
    const company = isValidTimeZone(companyTimeZone) ? companyTimeZone : DEFAULT_ZONE;
    const contactZone = isValidTimeZone(recipientTimeZone) ? recipientTimeZone : null;

    const [zone, setZone] = useState(company);
    const [start] = useState(() => instantToPicked(initialAt || nextQuarterHour(clock()), company));
    const [date, setDate] = useState(start.date);
    const [time, setTime] = useState(start.time);
    const [, setTick] = useState(0);
    const focusRef = useRef(null);
    // a minute passing can make the opening time stale: look again every few seconds
    useEffect(() => {
        const timer = setInterval(() => setTick((value) => value + 1), TICK_MS);
        return () => clearInterval(timer);
    }, []);

    const current = clock();
    const range = scheduleDayRange(current, zone);
    const today = range.first;
    const zones = scheduleZoneChoices({ companyTimeZone: company, recipientTimeZone: contactZone, now: current });

    const chosen = pickedToInstant(date, time, zone);
    const problem = chosen ? validateScheduledTime(chosen, current) : { ok: false, error: '' };
    let timeProblem = '';
    if (!time) timeProblem = 'Enter a time, for example 8:00 PM.';
    else if (!parseClock(time)) timeProblem = 'That time is not valid. Use a time like 8:00 PM.';
    else if (!chosen) timeProblem = 'That date or time is not valid.';
    else if (!problem.ok) timeProblem = problem.error;
    const shownError = timeProblem || error;
    const theirs = chosen ? recipientLocalLabel(chosen, contactZone, zone) : '';
    const canSchedule = Boolean(chosen) && problem.ok && !busy;

    const confirm = () => {
        const at = pickedToInstant(date, time, zone);
        if (!at || !validateScheduledTime(at, clock()).ok) { setTick((value) => value + 1); return; }
        onConfirm?.({ at, cancelOnReply: Boolean(initialCancelOnReply), timeZone: zone });
    };

    return (
        <Modal3D
            title={title}
            subtitle={subtitle}
            size="medium"
            portal={portal}
            onClose={onClose}
            initialFocusRef={focusRef}
            testId="schedule-panel"
            data-schedule-panel="true"
            {...modalProps}
            actions={(
                <>
                    <Button3D variant="light" onClick={onClose}>Cancel</Button3D>
                    <Button3D variant="go" disabled={!canSchedule} aria-busy={busy || undefined} onClick={confirm}>
                        {busy ? 'Scheduling…' : confirmLabel}
                    </Button3D>
                </>
            )}
        >
            <div className="ia-sched">
                <div className="ia-sched__zone" data-schedule-zone="true">
                    <label htmlFor={`${ids}-zone`}>Time zone</label>
                    <select id={`${ids}-zone`} className="ia-sched-field" value={zone} onChange={(event) => setZone(event.target.value)}>
                        {zones.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
                    </select>
                </div>
                {children && <div className="ia-sched__extra">{children}</div>}
                <div className="ia-sched__body">
                    <ScheduleCalendar value={date} range={range} today={today} onSelect={setDate} focusRef={focusRef} idPrefix={`${ids}-cal`} />
                    <div className="ia-sched__side">
                        <label htmlFor={`${ids}-time`} className="ia-sched__label">
                            Time
                            <input
                                id={`${ids}-time`}
                                type="time"
                                step="60"
                                className="ia-sched-field"
                                value={time}
                                aria-invalid={shownError ? 'true' : undefined}
                                aria-describedby={shownError ? `${ids}-problem` : undefined}
                                onChange={(event) => setTime(event.target.value)}
                            />
                        </label>
                        {shownError && <p id={`${ids}-problem`} role="alert" className="ia-sched__error" data-schedule-error="true">{shownError}</p>}
                        {theirs && <p className="ia-sched__theirs" data-schedule-recipient-time="true">Recipient&apos;s local time: {theirs}</p>}
                        {chosen && problem.ok && (
                            <p className="ia-sched__note" role="status" data-schedule-summary="true">Goes out <strong>{formatScheduledWhen(chosen, zone)}</strong>.</p>
                        )}
                    </div>
                </div>
            </div>
        </Modal3D>
    );
}
